import styled from 'styled-components'
import { rem } from 'polished'
import { useCallback, useMemo, useState } from 'react'
import { useIntl } from 'react-intl'

import BodyText from 'LuxKit/Typography/BodyText'
import Flag from 'LuxKit/Flag'
import Modal from 'LuxKit/Modal/Modal'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import { type VisibleLanguageOption, getFullLocale, getVisibleLanguageOptions } from 'i18n/util/locale'
import { LabRemoteConfigKeys } from 'constants/lab'
import { useLabRemoteConfig } from 'hooks/FeatureFlag/useLabRemoteConfig'

const LanguageWarningLabel = styled.span`
  display: inline-flex;
  gap: ${rem(8)};
  align-items: center;
`

function getCountryCodeFromLocale(locale: string, languageOptions: Array<VisibleLanguageOption>): string | undefined {
  const fullLocale = getFullLocale(locale, languageOptions)
  if (fullLocale) return new Intl.Locale(fullLocale).region
}

function LocaleRedirectModal({ accountLocale, urlLocale, onKeepPageLanguage, onSwitchToAccountLanguage }: {
  accountLocale: string
  urlLocale: string
  onKeepPageLanguage: () => Promise<void> | void
  onSwitchToAccountLanguage: () => Promise<void> | void
}) {
  const [loading, setLoading] = useState(false)

  const intl = useIntl()
  const modalContext = useModalElementContext<'url' | 'auth'>()
  const languagesRemoteConfig = useLabRemoteConfig(LabRemoteConfigKeys.languages)
  const languageOptions = useMemo(
    () => getVisibleLanguageOptions(languagesRemoteConfig),
    [languagesRemoteConfig],
  )
  const onClose = useCallback(() => modalContext.resolve('url'), [modalContext])

  const title = intl.formatMessage({
    id: 'jVH8voIfhVh0RZcbF60E',
    defaultMessage: 'Choose your language',
    description: 'Title for a modal asking the customer to resolve a URL language and account language conflict',
  })
  const urlLanguageLabel =
    intl.formatDisplayName(urlLocale, { type: 'language' }) ?? urlLocale
  const authLanguageLabel =
    intl.formatDisplayName(accountLocale, { type: 'language' }) ?? accountLocale

  const accountCountry = getCountryCodeFromLocale(accountLocale, languageOptions)
  const urlCountry = getCountryCodeFromLocale(urlLocale, languageOptions)

  const switchToAuthLanguageLabel = `Switch to ${authLanguageLabel.split('(')[0]}`

  return (
    <Modal
      title={title}
      loading={loading}
      isPrimaryActionDisabled={loading}
      isSecondaryActionDisabled={loading}
      primaryActionText={
        <LanguageWarningLabel>
          {accountCountry ? <Flag countryCode={accountCountry} aria-hidden /> : null}
          {switchToAuthLanguageLabel}
        </LanguageWarningLabel>
      }
      secondaryActionText={
        <LanguageWarningLabel>
          {urlCountry ? <Flag countryCode={urlCountry} aria-hidden /> : null}
          {intl.formatMessage({
            id: 'mvniesvnqwYRAJMKgWqJ',
            defaultMessage: 'Keep page language',
            description: 'Secondary action to keep the language currently present in the URL',
          })}
        </LanguageWarningLabel>
      }
      onPrimaryActionClick={() => {
        setLoading(true)
        onSwitchToAccountLanguage()
      }}
      onSecondaryActionClick={() => {
        setLoading(true)
        onKeepPageLanguage()
      }}
      onClose={onClose}
    >
      <BodyText variant="large">
        {intl.formatMessage(
          {
            id: '4Z4zmNyyNhN2PC0qkMZ9',
            defaultMessage:
              'This page is in {urlLanguage}, but your account language is {authLanguage}.',
            description:
              'Body text explaining a URL language and account language conflict',
          },
          {
            urlLanguage: urlLanguageLabel.split('(')[0],
            authLanguage: authLanguageLabel.split('(')[0],
          },
        )}
      </BodyText>
    </Modal>
  )
}

export default LocaleRedirectModal
